<template>
  <div v-show="showModal" class="adn-modal">
    <div class="adn-modal__modal" :style="{ top: customTopPosition }" @click="cancel()">
      <transition name="adn-modal__dialog-">
        <div v-show="showModal" :class="dialogClassNames" @click.stop>
          <div
            class="adn-modal__content"
            role="dialog"
            aria-labelledby="adn-modal__title"
            aria-describedby="adn-modal__body"
          >
            <header v-if="isAutoClosable || title || $slots.title" class="adn-modal__header">
              <span
                v-if="isAllowedToClose && isAutoClosable"
                class="adn-modal__close-button adn-icon adn-icon--navigation-close"
                aria-label="Fermer"
                data-test-modal-close
                @click="cancel()"
              />
              <h1 v-if="title || $slots.title" id="adn-modal__title" class="adn-modal__title" data-test-modal-title>
                <slot name="title">
                  {{ title }}
                </slot>
              </h1>
            </header>
            <section id="adn-modal__body" class="adn-modal__body">
              <!-- @slot Main part of the modal -->
              <slot name="body" :show-body="showModal">{{ body || message }}</slot>
            </section>
            <footer v-if="$slots.footer" class="adn-modal__footer" data-test-modal-footer>
              <!-- @slot Footer of the modal -->
              <slot name="footer"></slot>
            </footer>
          </div>
        </div>
      </transition>
    </div>
    <div class="adn-modal__background" @click="cancel()"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export interface ModalToShow {
  show: () => void
  hide: () => void
}

export default defineComponent({
  name: 'AdnModal',
  props: {
    isAutoClosable: {
      type: Boolean,
      default: () => true,
    },
    isAllowedToClose: {
      type: Boolean,
      default: true,
    },
    title: String,
    message: {
      type: String,
      default: () => '',
    },
    body: {
      type: String,
      default: () => '',
    },
    size: {
      type: String,
      default: () => 'normal',
      validator: (val: string) => ['small', 'normal', 'large', 'extra-large'].includes(val),
    },
    moveToBody: {
      type: Boolean,
      default: true,
    },
    customTopPosition: {
      type: String,
      default: '0',
    },
  },
  data() {
    return {
      showModal: false,
    }
  },
  computed: {
    dialogClassNames() {
      const classNames = ['adn-modal__dialog']
      if (this.size && this.size !== 'normal') {
        classNames.push(`adn-modal__dialog--${this.size}`)
      }
      return classNames
    },
  },
  beforeUnmount() {
    // @ts-ignore à revoir après la migration Vue3
    this.moveModalToParentComponent()
    document.body.classList.remove('modal-open')
  },
  methods: {
    cancel() {
      if (this.isAllowedToClose) this.hide()
    },
    hide() {
      if (this.moveToBody) {
        this.moveModalToParentComponent()
        document.body.classList.remove('modal-open')
      }
      if (this.showModal) {
        this.showModal = false
        this.$emit('afterHide')
      }
    },
    show() {
      if (!this.showModal) {
        this.showModal = true
        this.$emit('beforeShow')
      }
      if (this.moveToBody) {
        document.body.classList.add('modal-open')
        this.moveModalToBody()
      }
    },
    moveModalToBody() {
      const container = document.body
      if (container) {
        container.appendChild(this.$el)
      }
    },
    moveModalToParentComponent() {
      if (this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el)
      }
    },
  },
})
</script>

<style lang="scss">
.adn-modal {
  position: fixed;
  z-index: 1040;
}

.adn-modal__background {
  position: fixed;
  z-index: 1040;
  top: 0;
  left: 0;
  background: black;
  transition: opacity 0.2s linear;
  overflow: hidden;
  width: 100vw;
  height: 100vh;
  opacity: 0.35;
}

.adn-modal__modal {
  position: fixed;
  z-index: 1050;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden auto;
  outline: 0;
}

.adn-modal__dialog {
  position: relative;
  display: flex;
  align-items: center;
  width: auto;
  pointer-events: none;
  margin: 0.5rem;
  min-height: calc(100% - 4rem);

  @media (min-width: $breakpoint-sm) {
    max-width: 52rem;
    margin: 2rem auto;
  }

  &::before {
    display: block;
    height: calc(100vh - 4rem);
    content: '';
  }
}

.adn-modal__dialog--small {
  @media (min-width: $breakpoint-xxs) {
    max-width: 35rem;
  }
}

.adn-modal__dialog--large {
  @media (min-width: $breakpoint-md) {
    max-width: 70rem;
  }
}

.adn-modal__dialog--extra-large {
  @media (min-width: $breakpoint-md) {
    max-width: 92rem;
  }
}

.adn-modal__dialog--enter,
.adn-modal__dialog--leave-to {
  opacity: 0;
}

.adn-modal__dialog--enter-active,
.adn-modal__dialog--leave-active {
  transition: opacity 0.5s ease;
}

.adn-modal__header {
  padding-bottom: var(--spacing-3);
}

.adn-modal__title {
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);
  font-weight: bold;
  margin: 0;
}

.adn-modal__close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;

  &:hover {
    color: var(--color-main-dark);
  }
}

.adn-modal__content {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  padding: 2rem;
  pointer-events: auto;
  background-color: white;
  background-clip: padding-box;
  box-shadow: var(--shadow-dark);
  color: var(--color-text);
  outline: 0;
  border-radius: var(--border-radius-2);
}

.adn-modal__footer {
  padding-top: 2rem;
  text-align: center;

  @media (min-width: $breakpoint-xs) {
    text-align: right;
  }

  .adn-button {
    display: inline-block;
    margin: 0.5rem 0;
    width: 100%;

    @media (min-width: $breakpoint-xs) {
      margin: 0;
      width: auto;
    }
  }

  @media (min-width: $breakpoint-xs) {
    .adn-button + .adn-button {
      margin-left: 2rem;
    }
  }

  @media (min-width: $breakpoint-lg) {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .adn-button + .adn-button {
      margin-left: 2rem;
    }
  }
}
</style>
